// frontend/src/api/apiClient.js

import axios from 'axios';

const apiClient = axios.create({
  // I produktion: relativ URL (samma server)
  // I utveckling: REACT_APP_API_URL eller localhost:5000
  baseURL: process.env.NODE_ENV === 'production'
    ? ''
    : (process.env.REACT_APP_API_URL || 'http://localhost:5000'),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor för att lägga till JWT-token i headers
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Exportera login-funktionen
export const login = async (username, password) => {
  const response = await apiClient.post('/auth/login', { username, password });
  return response.data;
};

// ============================================================================
// Humla API - Image Integration
// ============================================================================

/**
 * List all images for a session
 * @param {string} sessionId - Session ID
 * @returns {Promise<{success: boolean, images: Array, count: number}>}
 */
export const listSessionImages = async (sessionId) => {
  const response = await apiClient.get(`/api/humlan/images/${sessionId}`);
  return response.data;
};

/**
 * Fetch latest images from Humla API
 * @param {string} sessionId - Session ID
 * @returns {Promise<{success: boolean, images: Array, count: number}>}
 */
export const refreshSessionImages = async (sessionId) => {
  const response = await apiClient.post(`/api/humlan/refresh/${sessionId}`);
  return response.data;
};

/**
 * Fetch image as blob and create object URL
 * Use this to display images securely with JWT authentication
 * @param {string} sessionId - Session ID
 * @param {string} filename - Image filename
 * @returns {Promise<string>} - Blob URL for image
 */
export const fetchImageBlob = async (sessionId, filename) => {
  const response = await apiClient.get(
    `/api/humlan/images/${sessionId}/${filename}`,
    { responseType: 'blob' }
  );
  return URL.createObjectURL(response.data);
};

/**
 * Delete all images for a session
 * @param {string} sessionId - Session ID
 * @returns {Promise<{success: boolean, deleted_count: number}>}
 */
export const deleteSessionImages = async (sessionId) => {
  const response = await apiClient.delete(`/api/humlan/images/${sessionId}`);
  return response.data;
};

// ============================================================================
// Chunk Status API - For tracking transcription progress
// ============================================================================

/**
 * Get chunk processing status for a session
 * @param {number} sessionId - Session ID
 * @returns {Promise<{total_chunks: number, completed_chunks: number, pending_chunks: number, all_processed: boolean, progress_percent: number}>}
 */
export const getChunkStatus = async (sessionId) => {
  const response = await apiClient.get(`/recorder/sessions/${sessionId}/chunk_status`);
  return response.data;
};

/**
 * Kassera kvarvarande (pending/failade) svans-chunks så journalen kan genereras
 * direkt utan att vänta in dem. Endast för svansen (gate:as på can_discard).
 * @param {number} sessionId - Session ID
 */
export const discardPendingChunks = async (sessionId) => {
  const response = await apiClient.post(`/recorder/sessions/${sessionId}/discard_pending`);
  return response.data;
};

// ============================================================================
// Session Status Subscription - For background retry polling
// ============================================================================

/**
 * Poll session status until all chunks are processed (including retries).
 * Returns cleanup function to stop polling.
 * @param {number} sessionId - Session ID
 * @param {function} onUpdate - Callback with status data
 * @param {number} timeoutMs - Max polling time (default 10 min)
 * @returns {function} Cleanup function to stop polling
 */
export const subscribeToSessionStatus = (sessionId, onUpdate, timeoutMs = 600000) => {
  let intervalId = setInterval(async () => {
    try {
      const data = await getChunkStatus(sessionId);
      onUpdate(data);
      if (data.all_processed && !data.has_pending_retries) {
        clearInterval(intervalId);
      }
    } catch (err) {
      console.error('Session status polling error:', err);
    }
  }, 5000);

  // Safety timeout
  const timeoutHandle = setTimeout(() => clearInterval(intervalId), timeoutMs);

  return () => {
    clearInterval(intervalId);
    clearTimeout(timeoutHandle);
  };
};

export default apiClient;
